import BaseService from './baseService';

export default class extends BaseService {
    constructor() {
        super("cubes");
    }

    // cube management
    /**
     * Gets Cube
     * @param {number} cubeId - Cube Id
     * @returns cube item
     */
    getCube = (cubeId: number) => this.httpGetRequest(`getCube/${cubeId}`);

    /**
     * Saves Cube
     * @param {Object} cube - cube item
     * @param {number | null} cube.cubeId - if edit then number, if create null
     * @param {string} cube.cubeName - cube name
     * @param {string | null} cube.description - description
     * @param {number} cube.taskId - task id
     * @returns created cube item
     */
    saveCube = (cube: {
        cubeId: number | null;
        cubeName: string;
        description: string | null;
        taskId: number;
    }) => this.httpPostRequest('saveCube', cube);

    /**
     * Deletes Cubes
     * @param {Array<number>} cubeIds - Cube Ids
     * @returns void
     */
    deleteCubes = (cubeIds: number[]) => this.httpPostRequest('deleteCubes', cubeIds);

    /**
     * Stops load/delete partition jobs
     * @param {Array<Object>} cubeJobs
     * @param {number} cubeJobs.scenarioId - Scenario Id
     * @param {number} cubeJobs.cubeId - Cube Id
     * @returns void
     */
    stopCubeJobs = (cubeJobs: { scenarioId: number; cubeId: number }[]) => this.httpPostRequest('stop', cubeJobs);

    /**
     * Restarts cube/scenario segment
     * @param {Object} item - cube job item
     * @param {number} item.taskId - task id
     * @param {number} item.scenarioId - scenario id
     * @param {number} item.cubeId - cube id
     * @param {Array<number>} item.segments - segments ids
     * @returns void
     */
    restartCubeJobs = (item: {
        taskId: number;
        scenarioId: number;
        cubeId: number;
        segments: number[];
    }) => this.httpPostRequest('restartJobs', item);

    /**
     * Restarts cube/scenario segment
     * @param {number} cubeId - cube Id
     * @returns void
     */
    restartCube = ({ cubeId }: { cubeId: number }) => this.httpPostRequest(`restart/${cubeId}`)

    /**
     * Gets Scenarios
     * @param {Object} filter 
     * @param {number} filter.pageNumber - page number
     * @param {number} filter.pageSize - page size
     * @param {number | null} filter.cubeId - cube id (if linked: true then cubeId: number and vice versa)
     * @param {boolean} filter.linked - linked
     * @param {number | null} filter.scenarioId - scenario id (if linked: true then scenarioId: number and vice versa)
     * @param {string} filter.searchString - search string
     * @param {boolean} filter.skipDetails - skip details
     * @param {number} filter.taskId - task id
     * @returns list of scenario item
     */
    getScenarios = (filter: {
        pageNumber: number;
        pageSize: number;
        cubeId?: number | null;
        linked?: boolean;
        scenarioId?: number | null;
        searchString?: string;
        skipDetails?: boolean;
        taskId?: number;
    }) => this.httpPostRequest('getScenarios', filter);

    /**
     * Gets Cubes
     * @param filter
     * @returns list of cubes
     */
    getCubes = (filter: { pageNumber: number; pageSize: number; taskId?: number; }) => this.httpPostRequest('getCubes', filter);

    /**
     * Get segments
     * @param {Object} filter 
     * @param {number} files.pageNumber - page number
     * @param {number} files.pageSize - page size
     * @param {number} files.cubeId - cube Id
     * @param {number} files.scenarioId - scenario Id
     * @returns list of segments
     */
    getPartitionsStatus = (filter: {
        pageNumber: number;
        pageSize: number;
        cubeId?: number;
        scenarioId?: number;
    }) => this.httpPostRequest('getPartitionsStatus', filter);

    /**
     * Gets Report 
     * @param {number} scenarioId - Scenario id
     * @param {number} cubeId - Cube id
     * @returns list of reports
     */
    getReports = (scenarioId: number, cubeId: number) => this.httpGetRequest(`getReports/${cubeId}/${scenarioId}`);

    /**
     * Saves reports changes
     * @param {Array<Object>} combinations 
     * @param {number} combinations.cubeId - cube id
     * @param {number} combinations.scenarioId - scenario id
     * @param {Array<Object>} reports 
     * @param {number} reports.fileId - file id
     * @param {string} reports.fileName - file name
     * @param {boolean} reports.hasAggregationScope - hasAggregationScope
     * @param {number} reports.scopeId - scope id
     * @returns void
     */
    saveReports = (
        combinations: {
            cubeId: number;
            scenarioId: number;
        }[],
        reports: {
            fileId: number;
            fileName: string;
            hasAggregationScope: boolean;
            scopeId: number;
        }[]) => this.httpPostRequest('saveReports', { combinations, reports });

    /**
     * Unlink scenario from cube
     * @param reports 
     * @param {number} reports.cubeId - cube id
     * @param {number} reports.scenarioId - scenario id
     * @returns void
     */
    deleteReports = (reports: { scenarioId: number; cubeId: number }[]) => this.httpPostRequest('deleteReports', reports);

    /**
     * Loads Cubes
     * @param {Array<number>} cubeIds - Cube ids
     * @returns void
     */
    loadCubes = (cubeIds: number[]) => this.httpPostRequest('loadCubes', cubeIds);

    /**
     * Loads Scenarios
     * @param {Array<number>} scenarioIds - Scenarios Ids
     * @returns 
     */
    loadScenarios = (scenarioIds: number[]) => this.httpPostRequest('loadScenarios', scenarioIds);

    getSegmentLogsUrl = (historyId: number) => this.httpGetRequest(`logs/${historyId}`);

    // templates 
    /**
     * Gets cube group
     * @param {number} templateId - template id
     * @returns template item
     */
    getTemplate = (templateId: number) => this.httpGetRequest(`template/${templateId}`);

    /**
     * Saves cube group
     * @param {Object} template 
     * @param {number} template.taskId - task id
     * @param {number | null} template.templateId - template Id will be number if edit and null if create
     * @param {string} template.templateName - cube group name
     * @param {string | null} template.description - cube group description
     * @returns Cube Group item
     */
    saveTemplate = (template: {
        taskId: number;
        templateName: string;
        description: string | null;
        templateId: number | null;
    }) => this.httpPutRequest('template', template);

    /**
     * Deletes cube groups
     * @param {Array<number>} templateIds - group ids
     * @returns void
     */
    deleteTemplates = (templateIds: number[]) => this.httpPostRequest('deleteTemplates', templateIds);

    /**
     * Gets cube groups
     * @param {Object} filter 
     * @param {number} filter.pageSize - page size
     * @param {number} filter.pageNumber - page number
     * @param {string} filter.searchString - search string
     * @param {number} filter.taskId - task id
     * @returns list of groups
     */
    getTemplates = (filter: {
        pageSize: number;
        pageNumber: number;
        searchString: string;
        taskId: number;
    }) => this.httpPostRequest('templatesList', filter);

    getTemplateReports = (templateId: number, cubeId: number) => this.httpGetRequest(`templateReports/${templateId}/${cubeId}`);

    /**
     * Saves default reports
     * @param {number} templateId - template Id
     * @param {Array<number>} cubeIds - cubeIds
     * @param {Array<Object>} reports - list of reports
     * @returns void
     */
    saveTemplateReports = (
        templateId: number,
        cubeIds: number[],
        reports: {
            fileId: number;
            fileName: string;
            hasAggregationScope: boolean;
            scopeId: number;
        }[]) => this.httpPutRequest('templateReports', { templateId, cubeIds, reports });

    deleteTemplateReports = (templateId: number, cubeIds: number[]) => this.httpPostRequest('deleteTemplateReports', { templateId, cubeIds });

    /**
     * Gets Memory Status Items
     * @param {Array<number>} cubeIds - Cubes ids
     * @param {Array<number>} scenarioIds- Scenarios ids
     * @returns memory status items
     */
    getMemoryStatuses = (params: { cubeIds: number[] } | { scenarioIds: number[] }) => this.httpPostRequest('memoryStatuses', { ...params });

    // default reports
    getDefaultReports = (taskId: number) => this.httpGetRequest(`defaultReports/${taskId}`);

    /**
     * Saves default reports
     * @param {number} taskId - task id 
     * @param {Array<Object>} reports - list of reports
     * @returns void
     */
    saveDefaultReports = (taskId: number, reports: {
        fileId: number;
        fileName: string;
        hasAggregationScope: boolean;
        scopeId: number;
    }[]) => this.httpPutRequest('defaultReports', { taskId, reports });
}
